import { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

import {
    buildSparseCurveAxisCategoriesForApex,
    resolveCurveAxisMaxTicks,
} from '../../utils/chart-axis-date';

export type {
    CreateDashboardBusinessLineApexOptionsParams,
    CreateHistoryBusinessLineApexOptionsParams,
} from './line-chart-of-business-options';
export {
    mergeBusinessLineApexOptions,
    createDashboardBusinessLineApexOptions,
    createHistoryBusinessLineApexOptions,
    resolveBusinessLineSeriesColors,
} from './line-chart-of-business-options';

/** 单条折线系列（与 Apex `series` 对齐） */
export interface BusinessLineSeriesItem {
    /** 系列名称（图例 / tooltip） */
    name: string;
    /** 与横轴类目对齐的数值点；`null` 表示断点 */
    data: (number | null)[];
}

/**
 * 营业折线图壳组件：只负责折线类型绑定、宽高与「百分比高度」容器。
 * Apex `options`（坐标轴、配色、tooltip 等）须由父组件用 {@link createDashboardBusinessLineApexOptions} /
 * {@link createHistoryBusinessLineApexOptions} 等组装后传入。
 */
export interface LinechartOfBusinessDataProps {
    /** 折线系列 */
    series: BusinessLineSeriesItem[];
    /** 完整 Apex options（通常含 `xaxis.categories`、`colors` 等） */
    apexOptions: ApexCharts.ApexOptions;
    /** 与 `apexOptions.chart.height` 保持一致；默认 `392` */
    height?: number | string;
    /** 外层容器 id；便于选择器 / 测试 */
    rootId?: string;
    /**
     * 原始横轴 x（与数据点等长）；传入后按容器宽度重算抽稀刻度并覆盖 `xaxis.categories`。
     * 须与创建 options 时传入的 `rawXLabels` 一致。
     */
    rawXLabels?: string[];
    /** 时间聚合粒度，如 `hour` / `day（配合 rawXLabels） */
    xAxisGranularity?: string;
    /** 横轴刻度上限；不传则按容器宽度自动估算 */
    xAxisMaxTicks?: number;
}

/**
 * 将业务系列映射为 Apex `line` 系列。
 */
function toApexLineSeries(items: BusinessLineSeriesItem[]) {
    return items.map((s) => ({
        name: s.name,
        data: s.data,
        type: 'line' as const,
    }));
}

const LinechartOfBusinessData = ({
    series,
    apexOptions,
    height = 392,
    rootId = 'line-chart-of-business-data',
    rawXLabels,
    xAxisGranularity,
    xAxisMaxTicks,
}: LinechartOfBusinessDataProps) => {
    /** 系列引用不变时可避免无谓重建 Apex 内部状态 */
    const apexSeries = useMemo(() => toApexLineSeries(series), [series]);

    /** 容器宽度，用于按屏宽估算横轴刻度数 */
    const rootRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = rootRef.current;
        if (!el) {
            return undefined;
        }
        const updateWidth = () => {
            setContainerWidth(el.getBoundingClientRect().width);
        };
        updateWidth();
        const observer = new ResizeObserver(() => {
            updateWidth();
        });
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, []);

    /** 按容器宽度重算横轴 categories（仅当传入 rawXLabels 时） */
    const mergedApexOptions = useMemo(() => {
        if (!rawXLabels?.length) {
            return apexOptions;
        }
        const maxTicks = resolveCurveAxisMaxTicks(
            rawXLabels.length,
            containerWidth,
            xAxisMaxTicks,
        );
        const categories = buildSparseCurveAxisCategoriesForApex({
            rawLabels: rawXLabels,
            granularity: xAxisGranularity,
            maxTicks,
        });
        return {
            ...apexOptions,
            xaxis: {
                ...apexOptions.xaxis,
                categories,
            },
        };
    }, [apexOptions, rawXLabels, xAxisGranularity, xAxisMaxTicks, containerWidth]);

    /** `height` 为百分比时外层须撑满父级，否则 Apex `100%` 高度解析失败 */
    const fillParentHeight = typeof height === 'string' && height.endsWith('%');

    return (
        <div
            ref={rootRef}
            id={rootId}
            style={{
                width: '100%',
                ...(fillParentHeight ? { height: '100%', minHeight: 0 } : {}),
            }}
        >
            <ReactApexChart
                options={mergedApexOptions}
                series={apexSeries}
                type="line"
                height={height}
                width="100%"
            />
        </div>
    );
};

export default LinechartOfBusinessData;
